import { UIProfile } from "../../../pages/stores/startupConfiguration"
import type { IFrameEvents } from "../IFrameEvents"
import { createParentMessenger } from "../WindowMessenger"
import { handleSetVolumeEvent } from "./setVolume"
import { handleRefreshCallEvent } from "./refreshCall"
import { initCallStateEvents } from "./callState"
import { initLocalMediaEvents, type LocalMediaEvents } from "./localMedia"
import type {
  AmnIncomingEvent,
  GetLocalMediaStateEvent,
  SetLocalMediaEvent,
  SetVolumeEvent,
} from "./incomingEvents"
import type { AmnOutgoingEvent } from "./outgoingEvents"
import { validateWindowMessage } from "./validation"
import {
  type InitialCallStateCache,
  initInitialCallStateCache,
} from "./initialCallStateCache"
import { parseAmnParams } from "../../../url/params/amn"

export const initAmnIFrameEvents = (uiProfile: UIProfile): IFrameEvents => {
  // eslint-disable-next-line no-console
  console.log("Initializing AMN IFrame Events")

  const { postMessage, registerMessageListener } =
    createParentMessenger<AmnOutgoingEvent>(uiProfile)

  const initialCallStateCache = initInitialCallStateCache(
    postMessage,
    parseAmnParams(window.location.href)
  )
  const callState = initCallStateEvents(postMessage)
  const localMedia = initLocalMediaEvents(postMessage)

  const routeIncomingMessage = (event: AmnIncomingEvent) => {
    const connectionAwareHandlers = createConnectionAwareHandlers(
      callState.isConnected(),
      localMedia,
      initialCallStateCache
    )

    switch (event.type) {
      case "amn:parent/getCallState":
        callState.handleGetCallStateEvent()
        break
      case "amn:parent/setVolume":
        connectionAwareHandlers.handleSetVolume(event)
        break
      case "amn:parent/refreshCall":
        handleRefreshCallEvent({
          isConnected: callState.isConnected(),
          initialCallStateCache,
        })
        break
      case "amn:parent/getLocalMediaState":
        connectionAwareHandlers.handleGetLocalMediaState(event)
        break
      case "amn:parent/setLocalMedia":
        connectionAwareHandlers.handleSetLocalMedia(event)
        break
      default:
        // eslint-disable-next-line no-console
        console.warn("Unhandled AMN message type", { event })
        break
    }
  }

  const handleWindowMessage = (event: MessageEvent) => {
    const amnEvent = validateWindowMessage(event)
    if (!amnEvent) {
      // eslint-disable-next-line no-console
      console.log("Ignoring unexpected AMN message", { event })
      return
    }
    // eslint-disable-next-line no-console
    console.log("Received AMN message", { amnEvent })
    routeIncomingMessage(amnEvent)
  }

  const unregisterMessageListener = registerMessageListener(handleWindowMessage)

  const onSessionConnected = () => {
    initialCallStateCache
      .synchronizeCallStateOnConnect()
      .finally(() => callState.onSessionConnected())
  }

  const dispose = () => {
    unregisterMessageListener()
    callState.dispose()
    localMedia.dispose()
  }

  return {
    getInitialCallState: initialCallStateCache.getInitialCallState,
    onSessionConnecting: callState.onSessionConnecting,
    onSessionConnected,
    onSessionConnectFailed: callState.onSessionConnectFailed,
    onSessionDisconnected: callState.onSessionDisconnected,
    dispose,
  }
}

type ConnectionAwareHandlers = {
  handleSetVolume: (event: SetVolumeEvent) => void
  handleSetLocalMedia: (event: SetLocalMediaEvent) => void
  handleGetLocalMediaState: (event: GetLocalMediaStateEvent) => void
}

/**
 * If we try to set state like volume or mute before the call is connected, it
 * will throw errors. To avoid this, we'll direct such requests to a cache
 * instead.
 */
export const createConnectionAwareHandlers = (
  connected: boolean,
  localMedia: LocalMediaEvents,
  initialCallStateCache: InitialCallStateCache
): ConnectionAwareHandlers =>
  connected
    ? {
        handleSetVolume: handleSetVolumeEvent,
        handleSetLocalMedia: localMedia.handleSetLocalMediaEvent,
        handleGetLocalMediaState: localMedia.handleGetLocalMediaStateEvent,
      }
    : {
        handleSetVolume: initialCallStateCache.handleSetVolumeEvent,
        handleSetLocalMedia: initialCallStateCache.handleSetLocalMediaEvent,
        handleGetLocalMediaState:
          initialCallStateCache.handleGetLocalMediaStateEvent,
      }
